import {
  Activity,
  ArrowUpDown,
  BarChart3,
  ChevronDown,
  CircleGauge,
  DatabaseZap,
  Languages,
  LogOut,
  Menu,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  SearchCheck,
  PanelsTopLeft,
  Sun,
  TrendingUp,
  X
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "wouter";
import { isDemoMode } from "../lib/api";

const nav = [
  { path: "/", key: "urls", label: "Hiệu suất URL", icon: PanelsTopLeft },
  { path: "/overview", key: "overview", icon: CircleGauge },
  { path: "/rankings", key: "rankings", icon: TrendingUp },
  { path: "/traffic", key: "traffic", icon: BarChart3 },
  { path: "/ranking-traffic", key: "matrix", icon: SearchCheck },
  { path: "/movers", key: "movers", icon: ArrowUpDown },
  { path: "/sync", key: "sync", icon: DatabaseZap }
];

export function Shell({
  children,
  onLogout
}: {
  children: React.ReactNode;
  onLogout: () => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("agrowth-sidebar-collapsed") === "true"
  );
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    const saved = localStorage.getItem("agrowth-theme");
    if (saved === "light" || saved === "dark") return saved;
    return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
  });
  const [location] = useLocation();
  const language = i18n.language.startsWith("en") ? "en" : "vi";
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("agrowth-theme", theme);
  }, [theme]);
  useEffect(() => {
    localStorage.setItem("agrowth-sidebar-collapsed", String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const switchLanguage = () => {
    const next = language === "vi" ? "en" : "vi";
    void i18n.changeLanguage(next);
    localStorage.setItem("agrowth-language", next);
  };

  return (
    <div className={`app-shell ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
      <aside className={menuOpen ? "sidebar open" : "sidebar"}>
        <div className="brand">
          <div className="brand-mark">
            <Activity size={20} />
          </div>
          <div>
            <strong>AGROWTH</strong>
            <span>{t("product")}</span>
          </div>
          <button className="mobile-close" onClick={() => setMenuOpen(false)} type="button">
            <X size={20} />
          </button>
        </div>
        <nav>
          <span className="nav-label">Workspace</span>
          {nav.map(({ path, key, label, icon: Icon }) => {
            const isActive = path === "/" ? location === "/" : location.startsWith(path);
            return (
            <Link
              key={key}
              href={path}
              onClick={() => setMenuOpen(false)}
              className={isActive ? "active" : ""}
            >
              <Icon size={18} />
              <span>{label ?? t(`nav.${key}`)}</span>
              <i />
            </Link>
          )})}
        </nav>
        <div className="sidebar-footer">
          <div className="data-source">
            <span className="status-dot" />
            <div>
              <strong>{isDemoMode ? t("demoData") : t("liveData")}</strong>
              <small>SERPRobot + GSC</small>
            </div>
          </div>
          <button type="button" className="account">
            <span>AG</span>
            <div>
              <strong>Agrowth Team</strong>
              <small>agrowth.io</small>
            </div>
            <ChevronDown size={15} />
          </button>
        </div>
      </aside>
      {menuOpen && <button className="backdrop" onClick={() => setMenuOpen(false)} />}
      <main>
        <header className="topbar">
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarCollapsed((value) => !value)}
            type="button"
            title={sidebarCollapsed ? t("common.showSidebar") : t("common.hideSidebar")}
            aria-label={sidebarCollapsed ? t("common.showSidebar") : t("common.hideSidebar")}
          >
            {sidebarCollapsed ? <PanelLeftOpen size={19} /> : <PanelLeftClose size={19} />}
          </button>
          <button className="menu-button" onClick={() => setMenuOpen(true)} type="button">
            <Menu size={21} />
          </button>
          <div className="topbar-context">
            <span>agrowth.io</span>
            <i>/</i>
            <strong>{t("product")}</strong>
          </div>
          <div className="topbar-actions">
            <span className={`environment-pill ${isDemoMode ? "demo" : ""}`}>
              <span />
              {isDemoMode ? t("demoData") : t("liveData")}
            </span>
            <button
              className="language-button theme-button"
              onClick={() => setTheme((value) => value === "dark" ? "light" : "dark")}
              type="button"
              title={theme === "dark" ? t("common.lightMode") : t("common.darkMode")}
              aria-label={theme === "dark" ? t("common.lightMode") : t("common.darkMode")}
            >
              {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
            </button>
            <button className="language-button" onClick={switchLanguage} type="button">
              <Languages size={17} />
              {language.toUpperCase()}
            </button>
            {!isDemoMode && (
              <button className="language-button" onClick={() => void onLogout()} type="button">
                <LogOut size={17} />
                {t("auth.logout")}
              </button>
            )}
          </div>
        </header>
        <div className="page-content">{children}</div>
      </main>
    </div>
  );
}
